import React from "react";
import { graphql, useFragment } from "react-relay";
import { useRouteMatch } from "react-router";

import { faArrowUpRightFromSquare } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";

import { inVideocall } from "~/lib/browserExtension";
import { inCalendarMode, inVideocallMode } from "~/lib/utils";
import { useShareModalFromUrl } from "~/meetings/hooks/useShareModalFromUrl";
import MeetingTitleAndXWCSwitcher, { MeetingTitle } from "~/meetings/MeetingHeader/MeetingTitleAndXWCSwitcher";
import JoinConferenceButton from "~/stream/NoteHeader/JoinConferenceButton";
import RecordingErrorOrRestrictedIndicator from "~/stream/NoteHeader/RecordingErrorOrRestrictedIndicator";
import { IconLinkButton } from "~/ui/button/IconButton";
import type { ModalProps } from "~/ui/Modal";
import Modal from "~/ui/Modal";

import Attendees from "./Attendees";
import MeetingDateOrTimer from "./MeetingDateOrTimer";
import MoreActionsMenu, { type MoreActionsProps } from "./MoreActionsMenu/MoreActionsMenu";
import SaveState from "./SaveState";
import SeriesExplorer from "./SeriesExplorer";
import ShareButton from "./ShareButton";

import type { MeetingHeader_meetingRecording$key } from "~/__generated__/MeetingHeader_meetingRecording.graphql";
import type { MeetingHeader_note$key } from "~/__generated__/MeetingHeader_note.graphql";
import type { MeetingHeader_viewer$key } from "~/__generated__/MeetingHeader_viewer.graphql";

import styles from "./MeetingHeader.module.css";

const viewerFragment = graphql`
	fragment MeetingHeader_viewer on Viewer {
		...MeetingTitleAndXWCSwitcher_viewer
		...JoinConferenceButton_viewer
		...Attendees_viewer
		...MoreActionsMenu_MeetingHeader_viewer
	}
`;

const noteFragment = graphql`
	fragment MeetingHeader_note on Note {
		id
		title
		noteType
		url
		viewerCanEdit

		stream {
			id
			...MeetingTitleAndXWCSwitcher_stream
		}

		event {
			guid
			...JoinConferenceButton_event
		}

		...SeriesExplorer_note
		...MeetingDateOrTimer_note
		...Attendees_note
		...ShareButton_note
		...MoreActionsMenu_MeetingHeader_note
	}
`;

const meetingRecordingFragment = graphql`
	fragment MeetingHeader_meetingRecording on MeetingRecording {
		id
		title
	}
`;

export default function MeetingHeader({
	viewer: viewerKey,
	note: noteKey,
	onClose,
	selectedRecording: meetingRecordingKey,
	...moreActionsProps
}: Omit<MoreActionsProps, "selectedRecordingId"> & {
	viewer: MeetingHeader_viewer$key;
	note: MeetingHeader_note$key | null;
	onClose?: ModalProps["onClose"];
	selectedRecording: MeetingHeader_meetingRecording$key | null;
}) {
	const viewer = useFragment(viewerFragment, viewerKey);
	const note = useFragment(noteFragment, noteKey);
	const selectedRecording = useFragment(meetingRecordingFragment, meetingRecordingKey);
	const inModal = !!onClose;

	const RootElement = inModal ? Modal.Header : "div";
	const rootElementProps = inModal ? { noBottomBorder: true } : {};

	const HeaderActionsElement = inModal ? Modal.HeaderActions : "div";
	const headerActionsElementProps = inModal ? { onClose } : {};

	const inCompanionMode = useRouteMatch({ path: "/companion-mode/" }) !== null;

	// Use the hook to handle URL parameter detection for triggering share modal
	useShareModalFromUrl(note?.id || null);

	const showIconLinkButtonInHeader = note && !inVideocall && inModal && note.viewerCanEdit && note.url;
	const showMoreActionsMenuInHeader = !inVideocall && !inCompanionMode;

	if (!note) {
		return (
			<RootElement className={styles.meetingHeader} {...rootElementProps}>
				<div className={cx(styles.leftSide, { [styles.inCompanionMode]: inCompanionMode })}>
					{!inCompanionMode && <MeetingTitle meetingTitle={selectedRecording?.title ?? "(Untitled recording)"} />}
				</div>
				<div className={styles.rightSide}>
					<HeaderActionsElement {...headerActionsElementProps}></HeaderActionsElement>
				</div>
			</RootElement>
		);
	}

	return (
		<RootElement className={styles.meetingHeader} {...rootElementProps}>
			<div className={cx(styles.leftSide, { [styles.inCompanionMode]: inCompanionMode })}>
				{!inModal && !inCompanionMode && <SeriesExplorer note={note} />}
				<RecordingErrorOrRestrictedIndicator eventGuid={note.event?.guid} />
				{!inCompanionMode && (
					<MeetingTitleAndXWCSwitcher stream={note.stream} viewer={viewer} meetingTitle={note.title} />
				)}
				<MeetingDateOrTimer note={note} />
				{note.event && <JoinConferenceButton viewer={viewer} event={note.event} />}
			</div>
			<div className={styles.rightSide}>
				<Attendees viewer={viewer} note={note}>
					{/** TODO: When the note isn't rendered (i.e. when viewing the recap) we don't get any presence updates.
					 * Although presence state is handled by the AttendeePresenceContext, the multicursor plugin is responsible for
					 * the websocket that updates it. So at some point we should probably move it out of there, or otherwise
					 * reevaluate how we keep this updated, and how we present that information.
					 */}
					<SaveState />
				</Attendees>
				{note.viewerCanEdit && <ShareButton note={note} />}
				{(showIconLinkButtonInHeader || showMoreActionsMenuInHeader) && (
					<HeaderActionsElement {...headerActionsElementProps}>
						{showIconLinkButtonInHeader && (
							<IconLinkButton
								icon={faArrowUpRightFromSquare}
								variant="tertiary"
								to={note.url}
								onClick={onClose}
								{...(inVideocallMode || inCalendarMode ? { target: "_blank", rel: "noopener" } : {})}
								className={styles.openFullViewIcon}
							/>
						)}
						{showMoreActionsMenuInHeader && (
							<MoreActionsMenu
								viewer={viewer}
								note={note}
								inModal={inModal}
								selectedRecordingId={selectedRecording?.id}
								{...moreActionsProps}
							/>
						)}
					</HeaderActionsElement>
				)}
			</div>
		</RootElement>
	);
}
